import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { InvoiceLineItemType, SettlementLineItemType } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { snakeCase, startCase } from 'lodash-es';
import React from 'react';

import LinkableText from '~components/LinkableText/LinkableText';
import { NET_RATE_FRACTION_DIGITS } from '~constants/consts';
import { SettlementLineItem } from '~hooks/useSettlementLineItems/models';
import { Settlement } from '~hooks/useSettlements/models';

import { buildFilesSearchUrl } from '../helper';
import { GridConfigType } from './constants';
import { JoinedListCell } from './JoinedListCell';
import { QuickBooksItemCell, QuickBooksItemColumn } from './QuickBooksItemCell';

function formatQuantities(quantities: SettlementLineItem['quantities']) {
  return quantities
    .filter((qty) => qty.quantity > 0)
    .map((qty) => `${qty.quantity} ${qty.unitOfMeasure}`)
    .join(' | ');
}

interface SettlementLineItemsProps {
  lineItems: SettlementLineItem[];
  settlement?: Settlement;
  currencyFormatter: (value: number, options?: Intl.NumberFormatOptions) => string;
  gridConfig: GridConfigType;
  isRebill?: boolean;
  showNetColumns?: boolean;
  /** When true, job-level data (Date, Truck #, Equipment) is hidden for non-first rows */
  isGroupedView?: boolean;
  /** Array of booleans indicating whether each line item is the first in its job group */
  isFirstInJobGroup?: boolean[];
  /** Set when the QuickBooks item column is shown */
  quickBooksItemColumn?: QuickBooksItemColumn;
}

export function SettlementLineItems({
  lineItems,
  settlement,
  currencyFormatter,
  gridConfig,
  isRebill = false,
  showNetColumns = false,
  isGroupedView = false,
  isFirstInJobGroup = [],
  quickBooksItemColumn,
}: SettlementLineItemsProps) {
  const theme = useTheme();

  return lineItems.map((lineItem, index) => {
    const uniqueKey = lineItem.date.toISOString() + JSON.stringify(lineItem.ids);
    // In grouped view, only show job-level data on the first row of each job group
    const showJobLevelData = !isGroupedView || (isFirstInJobGroup[index] ?? true);

    const rateOrUnit =
      lineItem.lineItemType === InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM
        ? lineItem.materialRateUnitOfMeasure
          ? `Per ${startCase(lineItem.materialRateUnitOfMeasure)}`
          : ''
        : lineItem.rateTypes
            .map((type) => t(`rate_types.${snakeCase(type)}`))
            .join(' | ');
    const timeRange =
      settlement?.settlementLineItemType === SettlementLineItemType.JOB
        ? [lineItem.payStartTime?.format('h:mmA'), lineItem.payEndTime?.format('h:mmA')]
            .filter(Boolean)
            .join(' - ')
        : '';
    const rateAndTimeParts = [rateOrUnit, timeRange].filter(Boolean);
    const netRate = isRebill
      ? lineItem.rebillPayCommissionNetRate
      : lineItem.payCommissionNetRate;
    const totalAmount = isRebill
      ? (lineItem.rebillNetTotalAmount ?? lineItem.rebillTotalAmount ?? 0)
      : (lineItem.netTotalAmount ?? lineItem.totalAmount);

    return (
      <Box
        key={uniqueKey}
        sx={{
          ...gridConfig,
          borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
        }}
      >
        {/* When adding columns, update the SettlementDetails component to add the new column headers */}
        <Box>
          <Typography variant="body2">
            {showJobLevelData
              ? dayjs.tz(lineItem.date.toISOString()).format('DD-MMM-YY')
              : ''}
          </Typography>
          {showJobLevelData &&
            (() => {
              const uniqueJobIds = [
                ...new Set(lineItem.ids.map(({ friendlyJobId }) => friendlyJobId)),
              ];
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  {uniqueJobIds.map((jobId, i) => (
                    <React.Fragment key={jobId}>
                      <LinkableText
                        href={buildFilesSearchUrl(jobId)}
                        sx={{
                          fontSize: '10px',
                          color: theme.brandV2.colors.treadGray1,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                          cursor: 'pointer',
                        }}
                      >
                        {jobId}
                      </LinkableText>
                      {i < uniqueJobIds.length - 1 && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '10px',
                            color: theme.brandV2.colors.treadGray1,
                          }}
                        >
                          {' | '}
                        </Typography>
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              );
            })()}
        </Box>
        {showJobLevelData ? (
          <JoinedListCell items={lineItem.equipmentNames} />
        ) : (
          <Typography variant="body2" />
        )}
        {showJobLevelData ? (
          <JoinedListCell items={lineItem.equipmentTypes} emptyValue="" />
        ) : (
          <Typography variant="body2" />
        )}
        {showJobLevelData ? (
          <JoinedListCell items={lineItem.truckExternalIds} emptyValue="" />
        ) : (
          <Typography variant="body2" />
        )}
        <JoinedListCell items={lineItem.materials} emptyValue="" />
        {settlement?.settlementLineItemType === SettlementLineItemType.LOAD && (
          <JoinedListCell items={lineItem.ticketNumbers} sx={{ gridColumn: 'span 2' }} />
        )}
        <Typography variant="body2">
          {lineItem.loadsCount > 0 &&
            t('approvals.driver_pay.settlements.pdf.num_loads', {
              num: lineItem.loadsCount,
            })}
          {lineItem.loadsCount > 0 && formatQuantities(lineItem.quantities) && ', '}
          {formatQuantities(lineItem.quantities)}
        </Typography>
        <Typography variant="body2">
          {lineItem.lineItemType === InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM
            ? t('approvals.driver_pay.settlements.pdf.material')
            : t('approvals.driver_pay.settlements.pdf.freight')}
        </Typography>
        <JoinedListCell items={rateAndTimeParts} separator=" " emptyValue="" />
        <Typography variant="body2">{lineItem.totalQuantity.toFixed(2)}</Typography>
        <Typography variant="body2">
          {currencyFormatter(
            isRebill ? (lineItem.rebillRateValue ?? 0) : (lineItem.rateValue ?? 0),
          )}
        </Typography>
        {showNetColumns && (
          <>
            <Typography variant="body2">
              {lineItem.payCommissionPercentage
                ? `${Number(lineItem.payCommissionPercentage)}%`
                : ''}
            </Typography>
            <Typography variant="body2">
              {netRate
                ? currencyFormatter(Number(netRate), {
                    maximumFractionDigits: NET_RATE_FRACTION_DIGITS,
                  })
                : ''}
            </Typography>
          </>
        )}
        <Typography variant="body2" sx={{ gridColumn: 'span 2' }}>
          {currencyFormatter(totalAmount)}
        </Typography>
        {quickBooksItemColumn && (
          <Box sx={{ gridArea: 'qb' }}>
            <QuickBooksItemCell
              areMatchesLoaded={quickBooksItemColumn.areMatchesLoaded}
              isMatching={quickBooksItemColumn.isMatching}
              isReadOnly={quickBooksItemColumn.isReadOnly}
              itemOptions={quickBooksItemColumn.itemOptions}
              lines={lineItem.settlementLineItemIds.map((settlementLineItemId) => ({
                settlementLineItemId,
              }))}
              matches={lineItem.settlementLineItemIds.flatMap(
                (settlementLineItemId) =>
                  quickBooksItemColumn.matchesBySettlementLineItemId.get(
                    settlementLineItemId,
                  ) ?? [],
              )}
              onRunMatching={quickBooksItemColumn.onRunMatching}
              onSelect={quickBooksItemColumn.onSelect}
            />
          </Box>
        )}
      </Box>
    );
  });
}
